import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Icons
import CheckIcon from '../../../../../icons/check.svg';

// Utils
import { getStopListSauceReplacements } from '../../../../../utils/cart';

// Portal
import Portal from '../../../Portal';

// Styles
import styles from './SaucesList.styl';

const cx = classNames.bind(styles);

export default function SaucesList(props) {
  const { isOpen, onHide, className, sauces, disabled, addFreeSauce, stopList, anchorEl } = props;

  const popperRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 0 });

  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  const visible = Boolean(isBrowser && isOpen && !disabled && anchorEl);

  const updatePosition = () => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const gap = 8;
    const left = Math.round(r.left - 24);
    const top = Math.round(r.bottom + gap);
    const minWidth = Math.max(r.width, 200);
    setPos({ top, left, minWidth });
  };

  useEffect(() => {
    if (!visible) return;
    updatePosition();

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, anchorEl]);

  useEffect(() => {
    if (!visible) return;

    const handleDocClick = e => {
      const t = e.target;
      if (popperRef.current?.contains(t)) return;
      if (anchorEl?.contains(t)) return;
      onHide();
    };

    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('touchstart', handleDocClick, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('touchstart', handleDocClick, { passive: true });
    };
  }, [visible, onHide, anchorEl]);

  if (!visible) return null;

  return (
    <Portal>
      <div
        ref={popperRef}
        className={cx('SaucesList', className, 'SaucesList--portal')}
        data-test-id="sauces_list"
        style={{
          position: 'fixed',
          top: pos.top,
          left: Math.max(8, pos.left),
          minWidth: pos.minWidth,
          maxWidth: 'calc(10vw - 16px)',
          zIndex: 99999,
        }}
      >
        {sauces.map(({ variations, name }) => {
          const { id } = variations[0];
          if (getStopListSauceReplacements(stopList, variations[0])) return null;

          return (
            <button
              key={name}
              className={cx('SaucesList__item')}
              onClick={() => {
                addFreeSauce(id);
                onHide();
              }}
              type="button"
            >
              <CheckIcon className={cx('SaucesList__check')} />
              <div className={cx('SaucesList__name')}>{name}</div>
            </button>
          );
        })}
      </div>
    </Portal>
  );
}

SaucesList.defaultProps = {
  className: '',
  isOpen: false,
  disabled: false,
  stopList: [],
  anchorEl: null,
};

SaucesList.propTypes = {
  className: PropTypes.string,
  sauces: PropTypes.array.isRequired,
  stopList: PropTypes.array,
  addFreeSauce: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  // Важно: без Element, чтобы не падать на сервере
  anchorEl: PropTypes.any,
};
